/**
 * The help desk, as this app reads and writes it.
 *
 * One module for the shapes, the vocabularies and the queries, so the list, the
 * ticket page and the five tabs all agree about what a ticket is. The legacy
 * app kept this in three zustand stores that each rebuilt the same request with
 * a different set of tenant parameters; there are no tenants here, so there is
 * one query per thing and react-query holds what came back.
 */

import { queryOptions } from "@tanstack/react-query";

import type { TranslationKey } from "@/constants/i18n";
import { api } from "@/services/api";
import type { Page } from "@/meta/types";

export type TicketStatus =
  "new" | "in_progress" | "in_review" | "on_hold" | "reopen" | "resolved" | "rejected";

export type TicketPriority = "low" | "medium" | "high";

export type TicketType = "new_request" | "enhancement" | "bug" | "other";

/** Four, because four is who reads this queue. See the enum on the API. */
export type TicketDepartment = "server" | "backend" | "frontend" | "ai";

export type TicketAssignment = "unassigned" | "support" | "employee";

export type CommentKind = "message" | "status" | "priority" | "assignment" | "update";

export type NoteKind = "resolution" | "activity_log";

/**
 * One file on a record, as it comes back.
 *
 * Two fields, because they are two different things: `url` is a signed link for
 * showing the file and it expires, `key` is the handle the record actually
 * holds. A form sends `key` back for a file the record already has — sending
 * the link back would store an address that stops working within the hour.
 */
export interface Attachment {
  name: string;
  key: string;
  url: string;
}

/**
 * One file on its way up: a handle, since by then it has been stored.
 */
export type AttachmentInput = { name: string; key: string };

/**
 * A file a form is holding.
 *
 * `file` is set while it is only chosen — picked from the disk, shown from a
 * local object URL, and not yet anywhere. It is uploaded when the record is
 * saved, not when it is picked: a file stored the moment somebody clicks
 * Attach is a file left behind in the bucket every time they change their mind
 * or close the form.
 */
export interface AttachmentDraft extends Attachment {
  file?: File;
}

/** A record's files, as the save should send them: by handle, every one. */
export const toAttachmentInput = (files: Attachment[]): AttachmentInput[] =>
  files.map((file) => ({ name: file.name, key: file.key }));

/**
 * Put files where a record can point at them.
 *
 * The file goes as itself, in a multipart body. It used to ride inside the
 * record's own JSON as a base64 `data:` URL, which is a third larger than the
 * file and put the whole body over the proxy's limit — so a photograph was
 * refused at the edge with a 413 the API never saw, and the screen showed
 * nothing at all.
 *
 * No `Content-Type` is set: only the browser can write a multipart boundary,
 * and the shared client already steps out of the way for a `FormData` body.
 */
export async function uploadAttachments(files: File[]): Promise<Attachment[]> {
  const form = new FormData();
  for (const file of files) form.append("files", file);
  return (await api.post<Attachment[]>("/helpdesk/attachments", form)).data;
}

/**
 * What a form holds, as what a save can send: the chosen files stored, the
 * rest left alone.
 *
 * Called from the save rather than from the picker, so nothing reaches the
 * bucket until somebody commits to it. The order is kept — a person who
 * attached three screenshots in an order meant that order.
 */
export async function uploadDrafts(drafts: AttachmentDraft[]): Promise<Attachment[]> {
  const pending = drafts.filter((draft) => draft.file);
  if (pending.length === 0) return drafts.map(({ file: _file, ...rest }) => rest);

  const stored = await uploadAttachments(pending.map((draft) => draft.file!));
  const byDraft = new Map(pending.map((draft, at) => [draft, stored[at]!]));
  return drafts.map(
    (draft) => byDraft.get(draft) ?? { name: draft.name, key: draft.key, url: draft.url },
  );
}

export interface TicketTask {
  id: number;
  reference: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  due_date: string | null;
}

/** A ticket as the list returns it: no body, no files — see `Ticket`. */
export interface TicketRow {
  id: number;
  reference: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  ticket_type: TicketType | null;
  department: TicketDepartment | null;
  due_date: string | null;
  assignment: TicketAssignment;
  assignee_id: number | null;
  assignee_name: string | null;
  parent_id: number | null;
  tasks: TicketTask[];
  comment_count: number;
  has_unread_comments: boolean;
  attachment_count: number;
  archived_at: string | null;
  created_by_id: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface Ticket extends TicketRow {
  description: string;
  attachments: Attachment[];
  parent: TicketTask | null;
}

export interface TicketCounts {
  total: number;
  new: number;
  in_progress: number;
  in_review: number;
  on_hold: number;
  reopen: number;
  resolved: number;
  rejected: number;
  overdue: number;
  archived: number;
}

export interface Comment {
  id: number;
  ticket_id: number;
  kind: CommentKind;
  body: string;
  attachments: Attachment[];
  created_by_id: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface Note {
  id: number;
  ticket_id: number;
  kind: NoteKind;
  title: string;
  description: string;
  attachments: Attachment[];
  created_by_id: number | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The list's address, named once.
 *
 * It is also the first element of the key `ResourcePage` builds for the rows,
 * which is why invalidating `HELPDESK_KEY` alone does not refresh them:
 * react-query compares a prefix element by element, and `"/helpdesk"` is not
 * `"/helpdesk/tickets"`.
 */
export const TICKETS_ENDPOINT = "/helpdesk/tickets";

/** Every read this module owns hangs off this, so one invalidate refreshes all of it. */
export const HELPDESK_KEY = ["/helpdesk"] as const;

export const ticketsKey = (params: Record<string, unknown>) =>
  [...HELPDESK_KEY, "tickets", params] as const;
export const ticketKey = (id: number) => [...HELPDESK_KEY, "ticket", id] as const;
export const summaryKey = [...HELPDESK_KEY, "summary"] as const;
export const commentsKey = (id: number) => [...HELPDESK_KEY, "ticket", id, "comments"] as const;
export const notesKey = (id: number, kind: NoteKind) =>
  [...HELPDESK_KEY, "ticket", id, "notes", kind] as const;

export const ticketQuery = (id: number) =>
  queryOptions({
    queryKey: ticketKey(id),
    queryFn: async ({ signal }) =>
      (await api.get<Ticket>(`/helpdesk/tickets/${id}`, { signal })).data,
  });

export const ticketSummaryQuery = () =>
  queryOptions({
    queryKey: summaryKey,
    queryFn: async ({ signal }) =>
      (await api.get<TicketCounts>("/helpdesk/tickets/summary", { signal })).data,
    // The cards are counts over the whole table and nobody watches them tick;
    // every write invalidates them, so this is only how long a quiet screen
    // waits before asking again.
    staleTime: 30_000,
  });

export const tasksQuery = (parentId: number) =>
  queryOptions({
    queryKey: ticketsKey({ parent_id: parentId }),
    queryFn: async ({ signal }) =>
      (
        await api.get<Page<TicketRow>>("/helpdesk/tickets", {
          params: { parent_id: parentId, size: 100, sort: "created_at", order: "asc" },
          signal,
        })
      ).data,
  });

export const commentsQuery = (ticketId: number) =>
  queryOptions({
    queryKey: commentsKey(ticketId),
    queryFn: async ({ signal }) =>
      (await api.get<Comment[]>(`/helpdesk/tickets/${ticketId}/comments`, { signal })).data,
    // Legacy held a websocket open per ticket for this. A thread is a handful
    // of rows and a conversation moves in minutes, not milliseconds; asking
    // every twenty seconds while the tab is in front of somebody costs one
    // small request and needs no socket, no reconnect loop and no server to
    // keep one alive. `refetchIntervalInBackground` is deliberately off, so a
    // tab left open overnight asks nothing.
    refetchInterval: 20_000,
  });

export const notesQuery = (ticketId: number, kind: NoteKind) =>
  queryOptions({
    queryKey: notesKey(ticketId, kind),
    queryFn: async ({ signal }) =>
      (
        await api.get<Note[]>(`/helpdesk/tickets/${ticketId}/notes`, {
          params: { kind },
          signal,
        })
      ).data,
  });

/**
 * The vocabularies, each as `value → translation key`.
 *
 * Written as objects rather than arrays so a row can label a value it is
 * holding without searching a list, and so a value the API adds that this build
 * does not know about is a type error here rather than a blank cell there.
 */
export const STATUS_LABEL: Record<TicketStatus, TranslationKey> = {
  new: "hd.statusNew",
  in_progress: "hd.statusInProgress",
  in_review: "hd.statusInReview",
  on_hold: "hd.statusOnHold",
  reopen: "hd.statusReopen",
  resolved: "hd.statusResolved",
  rejected: "hd.statusRejected",
};

/** What a status looks like. `danger` is rejection, not urgency. */
export const STATUS_TONE: Record<
  TicketStatus,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  new: "accent",
  in_progress: "accent",
  in_review: "warning",
  on_hold: "neutral",
  reopen: "warning",
  resolved: "success",
  rejected: "danger",
};

export const PRIORITY_LABEL: Record<TicketPriority, TranslationKey> = {
  low: "hd.priorityLow",
  medium: "hd.priorityMedium",
  high: "hd.priorityHigh",
};

export const PRIORITY_TONE: Record<TicketPriority, "neutral" | "warning" | "danger"> = {
  low: "neutral",
  medium: "warning",
  high: "danger",
};

export const TYPE_LABEL: Record<TicketType, TranslationKey> = {
  new_request: "hd.typeNewRequest",
  enhancement: "hd.typeEnhancement",
  bug: "hd.typeBug",
  other: "hd.typeOther",
};

export const DEPARTMENT_LABEL: Record<TicketDepartment, TranslationKey> = {
  server: "hd.deptServer",
  backend: "hd.deptBackend",
  frontend: "hd.deptFrontend",
  ai: "hd.deptAi",
};

export const ASSIGNMENT_LABEL: Record<TicketAssignment, TranslationKey> = {
  unassigned: "hd.assignUnassigned",
  support: "hd.assignSupport",
  employee: "hd.assignEmployee",
};

export const STATUSES = Object.keys(STATUS_LABEL) as TicketStatus[];
export const PRIORITIES = Object.keys(PRIORITY_LABEL) as TicketPriority[];
export const TYPES = Object.keys(TYPE_LABEL) as TicketType[];
export const DEPARTMENTS = Object.keys(DEPARTMENT_LABEL) as TicketDepartment[];
export const ASSIGNMENTS = Object.keys(ASSIGNMENT_LABEL) as TicketAssignment[];

/** Finished with. A closed ticket takes no comments and no new notes. */
export const isClosed = (status: TicketStatus) =>
  status === "resolved" || status === "rejected";

/**
 * Anything, which in HTML is no `accept` attribute at all.
 *
 * A help desk takes whatever the person in front of the problem has — a phone
 * photograph in whatever format that phone writes, a screen recording, a crash
 * dump, a spreadsheet — and a list of permitted types is a list of the ones
 * somebody thought of, which is never the one that matters at the time. The
 * API agrees; size is the only rule either end keeps.
 *
 * Absent rather than a wildcard: the two mean the same thing to a browser, but
 * a wildcard is a string other tools try to parse as a pattern, and at least
 * one reads the media-type wildcard as the prefix "star" and so matches
 * nothing at all.
 */
export const ATTACHMENT_ACCEPT = undefined;

export const ATTACHMENT_MAX_MB = 10;
export const ATTACHMENT_MAX_COUNT = 10;
